import { message } from 'antd';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  CreateScheduleDto,
  UpdateScheduleDto,
  LocationAssistantShiftSchedulesApi,
  LocationAssistantReportShiftsApi,
  UpdateScheduleShiftDto,
  ToolBagOwnerApi,
  CreateToolBagOwnerDto,
  ToggleScheduleDateIsWaitingForArrangeDto,
  LocationAssistantShiftScheduleSettingsApi,
  UpdateLocationAssistantShiftScheduleSettingDto,
} from '../api-client/api';

// Initialize the API client
const locationAssistantShiftSchedulesApi =
  new LocationAssistantShiftSchedulesApi();
const locationAssistantReportShiftsApi = new LocationAssistantReportShiftsApi();
const toolBagOwnerApi = new ToolBagOwnerApi();
const locationAssistantShiftScheduleSettingsApi =
  new LocationAssistantShiftScheduleSettingsApi();

// 獲取所有PT班表
export const usePTSchedules = (
  ...args: Parameters<
    typeof locationAssistantShiftSchedulesApi.locationAssistantShiftScheduleControllerFindSchedules
  >
) => {
  return useQuery(['ptSchedules'], async () => {
    const response =
      await locationAssistantShiftSchedulesApi.locationAssistantShiftScheduleControllerFindSchedules(
        ...args
      );

    return response.data;
  });
};

// 創建新的PT班表
export const useCreatePTSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: CreateScheduleDto) => {
      return locationAssistantShiftSchedulesApi.locationAssistantShiftScheduleControllerCreateSchedule(
        data
      );
    },
    {
      onSuccess: () => {
        message.success('班表建立成功');
        queryClient.invalidateQueries(['ptSchedules']);
      },
      onError: (error) => {
        message.error('班表建立失敗');
        console.error('Failed to create schedule:', error);
      },
    }
  );
};

// 更新PT班表
export const useUpdatePTSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: { id: string } & Partial<UpdateScheduleDto>) => {
      const { id, ...updateData } = data;
      return locationAssistantShiftSchedulesApi.locationAssistantShiftScheduleControllerUpdateSchedule(
        id,
        updateData as UpdateScheduleDto
      );
    },
    {
      onSuccess: () => {
        message.success('班表更新成功');
        queryClient.invalidateQueries(['ptSchedules']);
      },
      onError: (error) => {
        message.error('班表更新失敗');
        console.error('Failed to update schedule:', error);
      },
    }
  );
};

// 刪除PT班表
export const useDeletePTSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => {
      return locationAssistantShiftSchedulesApi.locationAssistantShiftScheduleControllerDeleteSchedule(
        id
      );
    },
    {
      onSuccess: () => {
        message.success('班表刪除成功');
        queryClient.invalidateQueries(['ptSchedules']);
      },
      onError: (error) => {
        message.error('班表刪除失敗');
        console.error('Failed to delete schedule:', error);
      },
    }
  );
};

export const usePTSchedule = (
  ...args: Parameters<
    typeof locationAssistantShiftSchedulesApi.locationAssistantShiftScheduleControllerFindSchedule
  >
) => {
  return useQuery({
    queryKey: ['ptSchedule', ...args],
    queryFn: async () => {
      const response =
        await locationAssistantShiftSchedulesApi.locationAssistantShiftScheduleControllerFindSchedule(
          ...args
        );

      return response.data;
    },
  });
};

export const useLocationAssistantReportShifts = (
  ...args: Parameters<
    typeof locationAssistantReportShiftsApi.locationAssistantReportShiftControllerFindReportedShift
  >
) => {
  return useQuery({
    queryKey: ['locationAssistantReportShifts', ...args],
    queryFn: async () => {
      const response =
        await locationAssistantReportShiftsApi.locationAssistantReportShiftControllerFindReportedShift(
          ...args
        );

      return response.data;
    },
    enabled: !!args[0] && !!args[1], // Only run the query if both startTime and endTime are provided
  });
};

export const useUpdatePTScheduleShift = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: { id: string } & UpdateScheduleShiftDto) => {
      const { id, ...updateData } = data;

      return locationAssistantShiftSchedulesApi.locationAssistantShiftScheduleControllerUpdateScheduleShift(
        id,
        updateData
      );
    },
    {
      onSuccess: () => {
        message.success('班表更新成功');
        queryClient.invalidateQueries(['ptSchedule']);
        queryClient.invalidateQueries(['toolBagOwners']);
      },
      onError: (error) => {
        message.error('班表更新失敗');
        console.error('Failed to update schedule:', error);
      },
    }
  );
};

export const useCreateToolBagOwner = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: CreateToolBagOwnerDto) => {
      return toolBagOwnerApi.toolBagOwnerControllerCreate(data);
    },
    {
      onSuccess: () => {
        message.success('更新成功');
        queryClient.invalidateQueries(['toolBagOwners']);
      },
    }
  );
};

export const useToolBagOwners = ({
  params,
  enabled = true,
}: {
  params: Parameters<typeof toolBagOwnerApi.toolBagOwnerControllerFindAll>;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ['toolBagOwners', ...params],
    queryFn: async () => {
      const response = await toolBagOwnerApi.toolBagOwnerControllerFindAll(
        ...params
      );

      return response.data;
    },
    enabled,
  });
};

export const useExportSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => {
      return locationAssistantShiftSchedulesApi
        .locationAssistantShiftScheduleControllerExportSchedule(id)
        .then((response) => {
          const data = Uint8Array.from((response.data.buffer as any).data);
          const blob = new Blob([data.buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'schedule.xlsx';
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        });
    },
    {
      onSuccess: () => {
        message.success('匯出成功');
        queryClient.invalidateQueries(['ptSchedules']);
      },
      onError: (error) => {
        message.error('匯出失敗');
        console.error('Failed to export schedule:', error);
      },
    }
  );
};

// 標記班表某班次需要安排更多人員
export const useToggleScheduleDateIsWaitingForArrange = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (
      data: { scheduleId: string } & ToggleScheduleDateIsWaitingForArrangeDto
    ) => {
      const { scheduleId, ...toggleData } = data;
      return locationAssistantShiftSchedulesApi.locationAssistantShiftScheduleControllerToggleScheduleDateIsWaitingForArrange(
        scheduleId,
        toggleData
      );
    },
    {
      onSuccess: () => {
        message.success('補人需求狀態更新成功');
        queryClient.invalidateQueries(['ptSchedule']);
        queryClient.invalidateQueries(['ptSchedules']);
      },
      onError: (error) => {
        message.error('補人需求狀態更新失敗');
        console.error(
          'Failed to toggle schedule date waiting for arrange:',
          error
        );
      },
    }
  );
};

export const usePtAdminSetting = () => {
  return useQuery({
    queryKey: ['ptAdminSetting'],
    queryFn: async () => {
      const response =
        await locationAssistantShiftScheduleSettingsApi.locationAssistantShiftScheduleSettingsControllerGetSettings();
      return response.data;
    },
    enabled: true,
  });
};

export const useUpdatePtAdminSetting = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (
      data: {
        key: string;
      } & UpdateLocationAssistantShiftScheduleSettingDto
    ) => {
      const { key, ...updateData } = data;

      return locationAssistantShiftScheduleSettingsApi.locationAssistantShiftScheduleSettingsControllerUpdateSetting(
        key,
        updateData
      );
    },
    {
      onSuccess: () => {
        message.success('設定更新成功');
        queryClient.invalidateQueries(['ptAdminSetting']);
      },
      onError: (error) => {
        message.error('設定更新失敗');
        console.error('Failed to update PT admin setting:', error);
      },
    }
  );
};
